import { AssignDeviceRequest, Device } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

type UseAssignDeviceActionProps = {
    deviceId: string;
};

type UseAssignDeviceActionData = {
    assignDevice: (data: AssignDeviceRequest) => Promise<Device>;
    loading: boolean;
    error: string;
};

/**
 * Assigning a scanned device to one of the viewer's own teams — the QR-code
 * landing page, which is reachable outside any company context.
 *
 * No `isAllowed`/`reason`: the only teams offered are the ones the viewer's own
 * company user lists, so there is no permission left to repeat here.
 */
export function useAssignDeviceAction(
    props: UseAssignDeviceActionProps
): UseAssignDeviceActionData {
    const { deviceId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/devices/{deviceId}/assign` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'INVALID_TEAM')) {
                return t('shared.errors.team_not_found');
            }
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: AssignDeviceRequest) =>
            openApiRef.current.devices
                .assignDevice(deviceId, data)
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            addOrUpdateAllDevicesCache(queryClient, device);

            UIToast.success(t('actions.assign_device.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        assignDevice: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
